import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Heart, Flower2, Leaf, ArrowRight, CheckCircle } from "lucide-react";

const VigilFuneralPlanning = () => {
  return (
    <>
      <Helmet>
        <title>Vigil & Funeral Planning Columbia, MO | End-of-Life Doula | Serenity Passage</title>
        <meta
          name="description"
          content="Vigil and funeral planning services in Columbia, Missouri. Honoring final wishes with dignity, environmental comfort planning, and meaningful rituals. End-of-life doula support in Mid-Missouri."
        />
        <meta
          name="keywords"
          content="vigil planning Columbia MO, funeral planning Columbia Missouri, end-of-life doula Mid-Missouri, green burial, natural burial, death doula"
        />
        <link rel="canonical" href="https://www.serenitypassage.net/vigil-and-funeral-planning" />
      </Helmet>

      {/* Hero Banner */}
      <section className="bg-gradient-to-b from-rose-100 to-rose-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-serif text-gray-800 text-center mb-4">
            Vigil & Funeral Planning
          </h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto">
            Honoring final wishes with intention, dignity, and compassionate presence
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-lg leading-relaxed mb-8">
              When death is near, thoughtful planning and compassionate support can transform the final passage into a meaningful, peaceful experience that honors the individual and brings comfort to loved ones.
            </p>

            <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
              <Heart className="w-7 h-7 text-rose-600" />
              Vigil Planning
            </h2>

            <p className="text-gray-700 mb-4">
              I support families in creating sacred space during the final hours and days. Vigil planning includes:
            </p>

            <ul className="space-y-4 mb-6">
              {[
                "Environmental comfort planning",
                "Creating a peaceful atmosphere with lighting, music, and aromatherapy",
                "Meaningful rituals that honor spiritual or cultural traditions",
                "Facilitating final conversations and goodbyes",
                "Supporting family members in being present without fear",
                "Continuous emotional support before, during, and after death",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-gray-700 mb-12 font-medium italic">
              Vigil presence available upon request.
            </p>

            <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
              <Flower2 className="w-7 h-7 text-rose-600" />
              Funeral & Life Celebration Planning
            </h2>

            <p className="text-gray-700 mb-6">
              I provide guidance in planning memorial services that authentically celebrate a life lived, from traditional funerals to unique celebrations.
            </p>

            <ul className="space-y-4 mb-12">
              {[
                "Discussing and documenting funeral preferences",
                "Creating personalized memorial service plans",
                "Coordinating with funeral homes and service providers",
                "Supporting family in decision-making",
                "Honoring cultural and spiritual traditions",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <h2 className="text-2xl font-serif text-gray-800 mb-6 flex items-center gap-3">
              <Leaf className="w-7 h-7 text-rose-600" />
              Green & Natural Burial Options
            </h2>

            <p className="text-gray-700 mb-6">
              For those seeking environmentally conscious alternatives, I provide information and guidance on green burial options.
            </p>

            <ul className="space-y-4 mb-12">
              {[
                "Natural burial grounds and green cemeteries",
                "Eco-friendly caskets and shrouds",
                "Conservation burial options",
                "Home funerals and family-directed services",
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-rose-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-rose-50 to-rose-100 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-serif text-gray-800 mb-4">
              Creating Peaceful Final Moments
            </h3>
            <p className="text-gray-600 mb-6">
              Your first 30-minute consultation is complimentary.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-8 py-4 rounded-full font-medium transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              Schedule a Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default VigilFuneralPlanning;
